import type {
  KeyConfig,
  KeyboardType,
} from "@eweadn-qiankun/shared-components";
import {
  applyDk63V2BottomRowToLayout63,
  getKeyCodeMatrix,
  isXingshanDk63V2Device,
  KEYBOARD_LAYOUTS,
  KEYCODE_MAP_HFD,
  KEYCODE_MAP_HFD_LAYOUT,
  resolveKeyPositionMapRow,
  resolveSdkMatrixRow,
  usesSliceFirstRowLayout,
} from "@eweadn-qiankun/shared-components";

/** 默认 WASD / 方向键 HID 码 */
const WASD_DEFAULT_KEYCODES = {
  KeyW: 26,
  KeyA: 4,
  KeyS: 22,
  KeyD: 7,
} as const;

const ARROW_DEFAULT_KEYCODES = {
  ArrowUp: 82,
  ArrowLeft: 80,
  ArrowDown: 81,
  ArrowRight: 79,
} as const;

const DEFAULT_KEYCODE_TO_WEB: Record<number, string> = {
  [WASD_DEFAULT_KEYCODES.KeyW]: "KeyW",
  [WASD_DEFAULT_KEYCODES.KeyA]: "KeyA",
  [WASD_DEFAULT_KEYCODES.KeyS]: "KeyS",
  [WASD_DEFAULT_KEYCODES.KeyD]: "KeyD",
  [ARROW_DEFAULT_KEYCODES.ArrowUp]: "ArrowUp",
  [ARROW_DEFAULT_KEYCODES.ArrowLeft]: "ArrowLeft",
  [ARROW_DEFAULT_KEYCODES.ArrowDown]: "ArrowDown",
  [ARROW_DEFAULT_KEYCODES.ArrowRight]: "ArrowRight",
};

const SWAP_PAIRS: Array<{ from: number; to: number }> = [
  { from: WASD_DEFAULT_KEYCODES.KeyW, to: ARROW_DEFAULT_KEYCODES.ArrowUp },
  { from: WASD_DEFAULT_KEYCODES.KeyA, to: ARROW_DEFAULT_KEYCODES.ArrowLeft },
  { from: WASD_DEFAULT_KEYCODES.KeyS, to: ARROW_DEFAULT_KEYCODES.ArrowDown },
  { from: WASD_DEFAULT_KEYCODES.KeyD, to: ARROW_DEFAULT_KEYCODES.ArrowRight },
  { from: ARROW_DEFAULT_KEYCODES.ArrowUp, to: WASD_DEFAULT_KEYCODES.KeyW },
  { from: ARROW_DEFAULT_KEYCODES.ArrowLeft, to: WASD_DEFAULT_KEYCODES.KeyA },
  { from: ARROW_DEFAULT_KEYCODES.ArrowDown, to: WASD_DEFAULT_KEYCODES.KeyS },
  { from: ARROW_DEFAULT_KEYCODES.ArrowRight, to: WASD_DEFAULT_KEYCODES.KeyD },
];

export type KeySlot = { row: number; col: number };

export type SetKeyCodeFn = (
  row: number,
  col: number,
  keycode: number,
  options?: { skipReload?: boolean }
) => Promise<boolean>;

export type SetKeyCodeBatchFn = (
  changes: Array<{ row: number; col: number; keycode: number }>
) => Promise<boolean>;

export type ApplyWasdArrowSwapOptions = {
  deferReload?: boolean;
  /** 华丰达 / X98-1K：一次 setKeyData 批量写入 */
  setKeyCodeBatch?: SetKeyCodeBatchFn;
};

export type WasdArrowSwapContext = {
  keyboardType: string;
  solutionRovider?: string;
  /** 贝盈 X98-1K：keyboardType=98 时需 displayname 解析 X98_1K 矩阵 */
  displayname?: string;
  /** DK63 V2 与公版 DK63 同为 keyboardType=63，须用 VID/PID 区分底行 */
  vendorId?: number;
  productId?: number;
};

/**
 * ES68 EVO 固件列（与 KEYBOARD_LAYOUTS["68EVO"] 一致）：
 * - Caps 行 A/S/D：col 1/2/3（勿误用 2/3/4，否则 ← 会写到 S）
 * - Shift 行 ↑：固件 col 13（布局表 col 12 为展示列，setKeyCode 须用 13）
 */
const PHYSICAL_SLOTS_68EVO: Partial<Record<number, KeySlot>> = {
  [WASD_DEFAULT_KEYCODES.KeyW]: { row: 2, col: 2 },
  [WASD_DEFAULT_KEYCODES.KeyA]: { row: 3, col: 1 },
  [WASD_DEFAULT_KEYCODES.KeyS]: { row: 3, col: 2 },
  [WASD_DEFAULT_KEYCODES.KeyD]: { row: 3, col: 3 },
  [ARROW_DEFAULT_KEYCODES.ArrowUp]: { row: 4, col: 13 },
  [ARROW_DEFAULT_KEYCODES.ArrowLeft]: { row: 5, col: 12 },
  [ARROW_DEFAULT_KEYCODES.ArrowDown]: { row: 5, col: 13 },
  [ARROW_DEFAULT_KEYCODES.ArrowRight]: { row: 5, col: 14 },
};

/** 归一化 keycode（华奋达 xsKeycode / value16 / HID 统一到 HID keycode） */
function normalizeComparableKeycode(code: number): number {
  if (!code) return 0;
  const hit = KEYCODE_MAP_HFD.find(
    (item) =>
      Number(item.keycode) === code ||
      Number((item as { xsKeycode?: number }).xsKeycode) === code ||
      Number(item.value16) === code
  );
  if (!hit) return code;
  return Number(hit.keycode ?? code);
}

function isArrowUpKeycode(code: number, webCode?: string): boolean {
  if (webCode === "ArrowUp") return true;
  return (
    normalizeComparableKeycode(code) === ARROW_DEFAULT_KEYCODES.ArrowUp ||
    code === ARROW_DEFAULT_KEYCODES.ArrowUp
  );
}

const SWAP_TARGET_KEYCODES = new Set<number>([
  WASD_DEFAULT_KEYCODES.KeyW,
  WASD_DEFAULT_KEYCODES.KeyA,
  WASD_DEFAULT_KEYCODES.KeyS,
  WASD_DEFAULT_KEYCODES.KeyD,
  ARROW_DEFAULT_KEYCODES.ArrowUp,
  ARROW_DEFAULT_KEYCODES.ArrowLeft,
  ARROW_DEFAULT_KEYCODES.ArrowDown,
  ARROW_DEFAULT_KEYCODES.ArrowRight,
]);

function findLayoutKeyByOriginalCell(
  layout: KeyConfig[][],
  row: number,
  col: number
): KeyConfig | null {
  for (const layoutRow of layout) {
    for (const key of layoutRow) {
      if (
        key.originalRow === row &&
        key.originalCol === col &&
        key.originalCol >= 0
      ) {
        return key;
      }
    }
  }
  return null;
}

/** 默认矩阵物理槽位（改键后 code 会变，不能靠 webCode 反查） */
function find68EvoDefaultFactorySlot(targetKeycode: number): KeySlot | null {
  const factoryMatrix = KEYBOARD_LAYOUTS["68EVO"];
  for (let row = 0; row < factoryMatrix.length; row++) {
    const matrixRow = factoryMatrix[row];
    if (!matrixRow) continue;
    for (let col = 0; col < matrixRow.length; col++) {
      if (matrixRow[col] !== targetKeycode) continue;
      return normalize68EvoSetKeySlot({ row, col }, targetKeycode);
    }
  }
  const hard = find68EvoPhysicalSlot(targetKeycode);
  return hard ? normalize68EvoSetKeySlot(hard, targetKeycode) : null;
}

function find68EvoSlot(
  _layout: KeyConfig[][],
  targetKeycode: number
): KeySlot | null {
  if (!SWAP_TARGET_KEYCODES.has(targetKeycode)) return null;
  return find68EvoDefaultFactorySlot(targetKeycode);
}

function normalize68EvoSetKeySlot(
  slot: KeySlot,
  targetKeycode: number
): KeySlot {
  if (
    targetKeycode === ARROW_DEFAULT_KEYCODES.ArrowUp &&
    slot.row === 4 &&
    slot.col === 12
  ) {
    return { row: 4, col: 13 };
  }
  return slot;
}

function resolveSliceLayoutKeyboardType(ctx: WasdArrowSwapContext): string {
  const displayname = String(ctx.displayname ?? "").trim();
  if (
    ctx.keyboardType === "68" &&
    (displayname === "ES68 EVO" || /ES68\s*EVO/i.test(displayname))
  ) {
    return "68EVO";
  }
  return ctx.keyboardType;
}

function find68EvoPhysicalSlot(targetKeycode: number): KeySlot | null {
  const slot = PHYSICAL_SLOTS_68EVO[targetKeycode];
  return slot ? { ...slot } : null;
}

const WASD_HID_KEYCODES = new Set<number>([26, 4, 22, 7]);

/** 贝盈改键/setKeyCode 以 KEYBOARD_LAYOUTS[row][col] 为准，勿用 LAYOUT_M80 等紧凑矩阵 */
const BEIYING_MECHA_KEYBOARD_TYPES = new Set([
  "m80",
  "m71",
  "t87",
  "x75v2",
  "X98_1K",
]);

const XINGSHAN_DIRECT_MATRIX_KEYBOARD_TYPES = new Set([
  "es87",
  "gamma75",
  "x75v3",
]);

/** WASD ↔ 方向键互换时的配对 HID（用于交换后定位物理格） */
const WASD_ARROW_SWAP_OPPOSITE: Record<number, number> = {
  [WASD_DEFAULT_KEYCODES.KeyW]: ARROW_DEFAULT_KEYCODES.ArrowUp,
  [WASD_DEFAULT_KEYCODES.KeyA]: ARROW_DEFAULT_KEYCODES.ArrowLeft,
  [WASD_DEFAULT_KEYCODES.KeyS]: ARROW_DEFAULT_KEYCODES.ArrowDown,
  [WASD_DEFAULT_KEYCODES.KeyD]: ARROW_DEFAULT_KEYCODES.ArrowRight,
  [ARROW_DEFAULT_KEYCODES.ArrowUp]: WASD_DEFAULT_KEYCODES.KeyW,
  [ARROW_DEFAULT_KEYCODES.ArrowLeft]: WASD_DEFAULT_KEYCODES.KeyA,
  [ARROW_DEFAULT_KEYCODES.ArrowDown]: WASD_DEFAULT_KEYCODES.KeyS,
  [ARROW_DEFAULT_KEYCODES.ArrowRight]: WASD_DEFAULT_KEYCODES.KeyD,
};

function layoutKeyMatchesFactorySlot(
  key: KeyConfig,
  targetKeycode: number,
  webCode?: string
): boolean {
  const hid = Number(key.keyCode);
  if (hid === targetKeycode) return true;
  if (webCode && key.code === webCode) return true;
  return WASD_ARROW_SWAP_OPPOSITE[targetKeycode] === hid;
}

/**
 * es87 / gamma75 / x75v3：静态 KEYBOARD_LAYOUTS 列可能与 SDK originalCol 不一致
 * （如 Shift 行 col1 占位导致 ↑ 实际在 col15 而非 col12）。
 * 优先用布局键 originalCol；交换后靠配对 HID 识别物理格。
 */
function findXingshanDirectMatrixSlot(
  layout: KeyConfig[][],
  layoutType: string,
  targetKeycode: number
): KeySlot | null {
  const refMatrix = getKeyCodeMatrix(layoutType as KeyboardType);
  const webCode = DEFAULT_KEYCODE_TO_WEB[targetKeycode];
  const staticSlot = findDefaultFactoryMatrixSlot(layoutType, targetKeycode);

  if (webCode) {
    for (const layoutRow of layout) {
      for (const key of layoutRow) {
        if (
          key.code !== webCode ||
          key.originalCol == null ||
          key.originalCol < 0
        ) {
          continue;
        }
        const row = key.originalRow ?? 0;
        const col = key.originalCol;
        const refAt = refMatrix[row]?.[col];
        if (refAt === targetKeycode) {
          return { row, col };
        }
        if (staticSlot && row === staticSlot.row && col !== staticSlot.col) {
          return { row, col };
        }
      }
    }
  }

  for (let row = 0; row < refMatrix.length; row++) {
    for (let col = 0; col < (refMatrix[row]?.length ?? 0); col++) {
      if (refMatrix[row][col] !== targetKeycode) continue;
      const layoutKey = findLayoutKeyByOriginalCell(layout, row, col);
      if (
        layoutKey &&
        layoutKey.originalCol != null &&
        layoutKey.originalCol >= 0
      ) {
        if (layoutKeyMatchesFactorySlot(layoutKey, targetKeycode, webCode)) {
          return {
            row: layoutKey.originalRow ?? row,
            col: layoutKey.originalCol,
          };
        }
        continue;
      }
      return { row, col };
    }
  }

  const pairedHid = WASD_ARROW_SWAP_OPPOSITE[targetKeycode];
  if (pairedHid != null) {
    for (const layoutRow of layout) {
      for (const key of layoutRow) {
        if (
          key.code === "__gap__" ||
          key.originalCol == null ||
          key.originalCol < 0
        ) {
          continue;
        }
        if (Number(key.keyCode) === pairedHid) {
          return {
            row: key.originalRow ?? 0,
            col: key.originalCol,
          };
        }
      }
    }
  }

  return staticSlot;
}

/**
 * 星闪：slice / 68EVO / EXX Alpha87·Gamma75·X75V3 等用 KEYBOARD_LAYOUTS[row][col] 写 setKeyCode。
 * 贝盈：同 KEYBOARD_LAYOUTS 矩阵。
 */
function usesFirmwareMatrixCoords(ctx: WasdArrowSwapContext): boolean {
  const vendor = String(ctx.solutionRovider ?? "");
  if (vendor === "beiying" || vendor === "rongyuan") return true;
  if (vendor !== "xingshan") return false;
  const layoutType = resolveSliceLayoutKeyboardType(ctx);
  if (usesSliceFirstRowLayout(layoutType)) return true;
  if (layoutType === "68EVO") return true;
  return XINGSHAN_DIRECT_MATRIX_KEYBOARD_TYPES.has(layoutType);
}

function findDefaultFactoryMatrixSlot(
  layoutType: string,
  targetKeycode: number,
  normalizeSlot?: (slot: KeySlot, keycode: number) => KeySlot
): KeySlot | null {
  const matrix = KEYBOARD_LAYOUTS[layoutType as keyof typeof KEYBOARD_LAYOUTS];
  if (!matrix?.length) return null;
  for (let row = 0; row < matrix.length; row++) {
    const matrixRow = matrix[row];
    if (!matrixRow) continue;
    for (let col = 0; col < matrixRow.length; col++) {
      if (matrixRow[col] !== targetKeycode) continue;
      const slot = { row, col };
      return normalizeSlot ? normalizeSlot(slot, targetKeycode) : slot;
    }
  }
  return null;
}

/** 华丰达 setKeyCode 用 KEYCODE_MAP_HFD_LAYOUT[row][col] 定位槽位，须与 SDK 矩阵一致 */
function usesHfdLayoutMatrix(ctx: WasdArrowSwapContext): boolean {
  if (String(ctx.solutionRovider ?? "") === "huafenda") return true;
  return ctx.keyboardType in KEYCODE_MAP_HFD_LAYOUT;
}

function getHfdLayoutMatrix(keyboardType: string): number[][] | undefined {
  const matrix =
    KEYCODE_MAP_HFD_LAYOUT[keyboardType as keyof typeof KEYCODE_MAP_HFD_LAYOUT];
  return matrix?.length ? matrix : undefined;
}

function findHfdMatrixSlot(
  keyboardType: string,
  targetKeycode: number
): KeySlot | null {
  const matrix = getHfdLayoutMatrix(keyboardType);
  if (!matrix) return null;
  for (let row = 0; row < matrix.length; row++) {
    const matrixRow = matrix[row];
    if (!matrixRow) continue;
    for (let col = 0; col < matrixRow.length; col++) {
      if (matrixRow[col] === targetKeycode) {
        return { row, col };
      }
    }
  }
  return null;
}

function usesBeiyingLayoutMatrix(ctx: WasdArrowSwapContext): boolean {
  return String(ctx.solutionRovider ?? "") === "beiying";
}

function resolveBeiyingLayoutKeyboardType(ctx: WasdArrowSwapContext): string {
  const keyboardType = ctx.keyboardType;
  if (
    String(ctx.solutionRovider ?? "") === "beiying" &&
    keyboardType === "98" &&
    String(ctx.displayname ?? "")
      .trim()
      .toLowerCase() === "x98"
  ) {
    return "X98_1K";
  }
  return keyboardType;
}

function findBeiyingMatrixSlot(
  ctx: WasdArrowSwapContext,
  targetKeycode: number
): KeySlot | null {
  const layoutType = resolveBeiyingLayoutKeyboardType(ctx);
  const matrix = KEYBOARD_LAYOUTS[layoutType as keyof typeof KEYBOARD_LAYOUTS];
  if (!matrix?.length) return null;
  for (let row = 0; row < matrix.length; row++) {
    const matrixRow = matrix[row];
    if (!matrixRow) continue;
    for (let col = 0; col < matrixRow.length; col++) {
      if (matrixRow[col] === targetKeycode) {
        return { row, col };
      }
    }
  }
  return null;
}

/** 星闪 slice / 贝盈：setKeyCode 直接用布局矩阵 row/col，勿经 UI KeyConfig 反查 */
function findFirmwareMatrixSlot(
  ctx: WasdArrowSwapContext,
  layout: KeyConfig[][],
  targetKeycode: number
): KeySlot | null {
  const layoutType = resolveSliceLayoutKeyboardType(ctx);
  if (layoutType === "68EVO") {
    const evoSlot = find68EvoSlot(layout, targetKeycode);
    if (evoSlot) return evoSlot;
  }
  if (XINGSHAN_DIRECT_MATRIX_KEYBOARD_TYPES.has(layoutType)) {
    return findXingshanDirectMatrixSlot(layout, layoutType, targetKeycode);
  }
  const matrix = getLayoutMatrix(layoutType, ctx);
  if (!matrix) return null;
  for (let row = 0; row < matrix.length; row++) {
    const matrixRow = matrix[row];
    if (!matrixRow) continue;
    for (let col = 0; col < matrixRow.length; col++) {
      if (matrixRow[col] === targetKeycode) {
        return { row, col };
      }
    }
  }
  return null;
}

/** setKeyCode 以布局矩阵 row/col 索引为准（华丰达 / 贝盈） */
function usesLayoutMatrixIndex(ctx: WasdArrowSwapContext): boolean {
  return usesHfdLayoutMatrix(ctx) || usesBeiyingLayoutMatrix(ctx);
}

/**
 * 63 与 68 默认矩阵列位不同：
 * - 68：21 列 KEYBOARD_LAYOUTS（含 Shift 行 gap）
 * - 公版 DK63：底行 RAlt、←、↓、→、Fn（共享 KEYBOARD_LAYOUTS["63"]）
 * - DK63 V2（PID 0x1c4d/0x1c49）：底行 RAlt、Fn、←、↓、→（仅此处覆盖，不改共享矩阵）
 */
function getLayoutMatrix63(ctx?: WasdArrowSwapContext): number[][] | undefined {
  const base = KEYBOARD_LAYOUTS["63"];
  if (ctx && isXingshanDk63V2Device(ctx.productId, ctx.vendorId)) {
    return applyDk63V2BottomRowToLayout63(base);
  }
  return base;
}

function getLayoutMatrix(
  keyboardType: string,
  ctx?: WasdArrowSwapContext
): number[][] | undefined {
  const resolvedType =
    ctx != null ? resolveSliceLayoutKeyboardType(ctx) : keyboardType;
  if (ctx && usesHfdLayoutMatrix(ctx)) {
    const hfdMatrix = getHfdLayoutMatrix(resolvedType);
    if (hfdMatrix) return hfdMatrix;
  }
  if (
    (ctx && usesBeiyingLayoutMatrix(ctx)) ||
    BEIYING_MECHA_KEYBOARD_TYPES.has(resolvedType)
  ) {
    const matrix =
      KEYBOARD_LAYOUTS[resolvedType as keyof typeof KEYBOARD_LAYOUTS];
    if (matrix?.length) return matrix;
  }
  if (resolvedType === "63") {
    return getLayoutMatrix63(ctx);
  }
  if (resolvedType === "M98" || resolvedType === "M98TFT") {
    const hfdMatrix = getHfdLayoutMatrix(resolvedType);
    if (hfdMatrix) return hfdMatrix;
    const matrix =
      KEYBOARD_LAYOUTS[resolvedType as keyof typeof KEYBOARD_LAYOUTS];
    if (matrix?.length) return matrix;
  }
  if (resolvedType === "huafenda87") {
    const hfdMatrix = getHfdLayoutMatrix(resolvedType);
    if (hfdMatrix) return hfdMatrix;
  }
  // SMART875(84) / DEEP80 MAX：Shift 行 col1 占位列，须用 KEYBOARD_LAYOUTS 而非紧凑矩阵
  if (resolvedType === "84" || resolvedType === "deep80max") {
    const matrix = KEYBOARD_LAYOUTS[resolvedType as "84" | "deep80max"];
    if (matrix?.length) return matrix;
  }
  if (usesSliceFirstRowLayout(resolvedType)) {
    const sliceMatrix =
      KEYBOARD_LAYOUTS[resolvedType as keyof typeof KEYBOARD_LAYOUTS];
    if (sliceMatrix?.length) return sliceMatrix;
  }
  try {
    const matrix = getKeyCodeMatrix(resolvedType as KeyboardType);
    if (matrix?.length) return matrix;
  } catch {
    /* fallback below */
  }
  return KEYBOARD_LAYOUTS[resolvedType as keyof typeof KEYBOARD_LAYOUTS];
}

function findLayoutKeyAtFirmwareCell(
  layout: KeyConfig[][],
  keyboardType: string,
  firmwareRow: number,
  firmwareCol: number
): { key: KeyConfig; layoutRowIndex: number } | null {
  for (
    let layoutRowIndex = 0;
    layoutRowIndex < layout.length;
    layoutRowIndex++
  ) {
    for (const key of layout[layoutRowIndex]) {
      if (
        key.code === "__gap__" ||
        key.originalCol == null ||
        key.originalCol < 0
      ) {
        continue;
      }
      const sdkRow = resolveSdkMatrixRow(key, layoutRowIndex, keyboardType);
      if (sdkRow === firmwareRow && key.originalCol === firmwareCol) {
        return { key, layoutRowIndex };
      }
    }
  }
  return null;
}

function findLayoutKeyByWebCode(
  layout: KeyConfig[][],
  webCode: string
): { key: KeyConfig; layoutRowIndex: number } | null {
  for (
    let layoutRowIndex = 0;
    layoutRowIndex < layout.length;
    layoutRowIndex++
  ) {
    for (const key of layout[layoutRowIndex]) {
      if (key.code === webCode) {
        return { key, layoutRowIndex };
      }
    }
  }
  return null;
}

function toSetKeyCodeSlot(
  key: KeyConfig,
  layoutRowIndex: number,
  ctx: WasdArrowSwapContext
): KeySlot {
  if (usesFirmwareMatrixCoords(ctx)) {
    return {
      row:
        resolveSdkMatrixRow(key, layoutRowIndex, ctx.keyboardType) ??
        key.originalRow ??
        layoutRowIndex,
      col: key.originalCol!,
    };
  }
  return {
    row: resolveKeyPositionMapRow(key, layoutRowIndex, ctx.keyboardType),
    col: key.originalCol!,
  };
}

/** 按默认布局矩阵定位物理键位（不受当前改键影响） */
export function findSlotForDefaultKeycode(
  layout: KeyConfig[][],
  ctx: WasdArrowSwapContext,
  targetKeycode: number
): KeySlot | null {
  if (usesHfdLayoutMatrix(ctx)) {
    return findHfdMatrixSlot(ctx.keyboardType, targetKeycode);
  }
  if (usesBeiyingLayoutMatrix(ctx)) {
    return findBeiyingMatrixSlot(ctx, targetKeycode);
  }
  if (usesFirmwareMatrixCoords(ctx)) {
    return findFirmwareMatrixSlot(ctx, layout, targetKeycode);
  }

  const matrix = getLayoutMatrix(ctx.keyboardType, ctx);
  if (matrix) {
    for (let firmwareRow = 0; firmwareRow < matrix.length; firmwareRow++) {
      const row = matrix[firmwareRow];
      if (!row) continue;
      for (let firmwareCol = 0; firmwareCol < row.length; firmwareCol++) {
        if (row[firmwareCol] !== targetKeycode) continue;
        const hit = findLayoutKeyAtFirmwareCell(
          layout,
          ctx.keyboardType,
          firmwareRow,
          firmwareCol
        );
        if (!hit) continue;
        return toSetKeyCodeSlot(hit.key, hit.layoutRowIndex, ctx);
      }
    }
  }

  const webCode = DEFAULT_KEYCODE_TO_WEB[targetKeycode];
  if (webCode && WASD_HID_KEYCODES.has(targetKeycode)) {
    const byCode = findLayoutKeyByWebCode(layout, webCode);
    if (byCode) {
      return toSetKeyCodeSlot(byCode.key, byCode.layoutRowIndex, ctx);
    }
  }

  return null;
}

function getKeyAtSlot(
  layout: KeyConfig[][],
  slot: KeySlot,
  ctx: WasdArrowSwapContext
): KeyConfig | null {
  // 华奋达：固件矩阵行（含 huafenda68 空第 0 行）；UI 可能已 slice，需用 resolveSdkMatrixRow
  if (usesHfdLayoutMatrix(ctx)) {
    const direct = findLayoutKeyByOriginalCell(layout, slot.row, slot.col);
    if (direct) return direct;
    for (
      let layoutRowIndex = 0;
      layoutRowIndex < layout.length;
      layoutRowIndex++
    ) {
      for (const key of layout[layoutRowIndex]) {
        if (
          key.code === "__gap__" ||
          key.originalCol == null ||
          key.originalCol < 0
        ) {
          continue;
        }
        const sdkRow = resolveSdkMatrixRow(
          key,
          layoutRowIndex,
          ctx.keyboardType
        );
        if (sdkRow === slot.row && key.originalCol === slot.col) {
          return key;
        }
      }
    }
    return null;
  }
  if (usesFirmwareMatrixCoords(ctx) || usesLayoutMatrixIndex(ctx)) {
    return findLayoutKeyByOriginalCell(layout, slot.row, slot.col);
  }
  for (
    let layoutRowIndex = 0;
    layoutRowIndex < layout.length;
    layoutRowIndex++
  ) {
    for (const key of layout[layoutRowIndex]) {
      if (key.code === "__gap__" || key.originalCol == null) continue;
      const apiSlot = toSetKeyCodeSlot(key, layoutRowIndex, ctx);
      if (apiSlot.row === slot.row && apiSlot.col === slot.col) {
        return key;
      }
    }
  }
  return null;
}

export function canSwapWasdArrow(
  layout: KeyConfig[][] | null,
  ctx: WasdArrowSwapContext
): boolean {
  if (!layout?.length) return false;
  return SWAP_PAIRS.every(
    ({ from }) => findSlotForDefaultKeycode(layout, ctx, from) != null
  );
}

/** W 物理键当前是否已映射为方向上 */
export function isWasdArrowSwapped(
  layout: KeyConfig[][],
  ctx: WasdArrowSwapContext
): boolean {
  const wSlot = findSlotForDefaultKeycode(
    layout,
    ctx,
    WASD_DEFAULT_KEYCODES.KeyW
  );
  if (!wSlot) return false;
  const wKey = getKeyAtSlot(layout, wSlot, ctx);
  if (!wKey) return false;
  return isArrowUpKeycode(Number(wKey.keyCode), String(wKey.code ?? ""));
}

export async function applyWasdArrowSwap(
  setKeyCode: SetKeyCodeFn,
  layout: KeyConfig[][],
  ctx: WasdArrowSwapContext,
  enable: boolean,
  options?: ApplyWasdArrowSwapOptions
): Promise<boolean> {
  if (options?.setKeyCodeBatch) {
    const slotChanges = new Map<
      string,
      { row: number; col: number; keycode: number }
    >();
    for (const { from, to } of SWAP_PAIRS) {
      const slot = findSlotForDefaultKeycode(layout, ctx, from);
      if (!slot) return false;
      slotChanges.set(`${slot.row}-${slot.col}`, {
        row: slot.row,
        col: slot.col,
        keycode: enable ? to : from,
      });
    }
    return options.setKeyCodeBatch(Array.from(slotChanges.values()));
  }

  for (let i = 0; i < SWAP_PAIRS.length; i++) {
    const { from, to } = SWAP_PAIRS[i];
    const slot = findSlotForDefaultKeycode(layout, ctx, from);
    if (!slot) return false;
    const targetKeycode = enable ? to : from;
    const skipReload =
      options?.deferReload === true || i < SWAP_PAIRS.length - 1;
    const success = await setKeyCode(slot.row, slot.col, targetKeycode, {
      skipReload,
    });
    if (!success) return false;
  }
  return true;
}
